/**
 * O2 Autocomplete Component
 *
 * Extended version of hlm-autocomplete with additional features:
 * - showClearBtn: Shows clear button when there's text OR a selected value (default: true)
 * - Built-in displayName support: No provider needed, uses displayName by default
 *
 * O2 CUSTOM: This component is a copy of hlm-autocomplete with O2-specific enhancements.
 * It's safe from spartan updates since it's in the ui-autocomplete-o2 folder.
 *
 * @example
 * ```typescript
 * // Your option type must have displayName
 * interface MyOption extends O2AutocompleteOptionBase {
 *   id: string;
 *   code: string;
 *   displayName: string; // Required!
 * }
 *
 * // Usage - no provider needed!
 * <o2-autocomplete
 *   [filteredOptions]="options()"
 *   (valueChange)="onSelected($event)"
 * />
 * ```
 */
import type { BooleanInput } from "@angular/cdk/coercion";
import { NgTemplateOutlet } from "@angular/common";
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  linkedSignal,
  model,
  OnDestroy,
  output,
  type TemplateRef,
  viewChild,
} from "@angular/core";
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { provideIcons } from "@ng-icons/core";
import {
  lucideChevronDown,
  lucideCircleX,
  lucideSearch,
} from "@ng-icons/lucide";
import {
  BrnAutocomplete,
  BrnAutocompleteImports,
} from "@spartan-ng/brain/autocomplete";
import { debouncedSignal } from "@spartan-ng/brain/core";
import type { ChangeFn, TouchFn } from "@spartan-ng/brain/forms";
import { BrnPopoverImports } from "@spartan-ng/brain/popover";
import { HlmIconImports } from "@spartan-ng/helm/icon";
import { HlmPopoverImports } from "@spartan-ng/helm/popover";
import { classes, hlm } from "@spartan-ng/helm/utils";
import type { ClassValue } from "clsx";
import {
  HlmAutocompleteEmpty,
  HlmAutocompleteGroup,
  HlmAutocompleteItem,
  HlmAutocompleteList,
  HlmAutocompleteTrigger,
} from "@o2-fe/ui";
import { HlmInputGroupImports } from "@spartan-ng/helm/input-group";

/**
 * Base interface for O2 Autocomplete options.
 * All option types MUST have a displayName property.
 * This ensures consistent display across all autocompletes without needing providers.
 *
 * @example
 * ```typescript
 * interface ProjectOption extends O2AutocompleteOptionBase {
 *   id: string;
 *   code: string;
 *   name: string;
 *   displayName: string; // Required - e.g., `${code} - ${name}`
 * }
 * ```
 */
export interface O2AutocompleteOptionBase {
  /** Display string shown in the autocomplete dropdown and input field */
  displayName: string;
}

/**
 * Default transform function that extracts displayName from option.
 * Used when no custom transform is provided.
 */
const defaultTransformToString = <T extends O2AutocompleteOptionBase>(
  option: T | null | undefined
): string => {
  if (!option) return "";
  return option.displayName ?? "";
};

export const O2_AUTOCOMPLETE_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => O2AutocompleteComponent),
  multi: true,
};

@Component({
  selector: "o2-autocomplete",
  standalone: true,
  imports: [
    NgTemplateOutlet,
    BrnAutocompleteImports,
    HlmAutocompleteEmpty,
    HlmAutocompleteGroup,
    HlmAutocompleteItem,
    HlmAutocompleteList,
    HlmAutocompleteTrigger,
    BrnPopoverImports,
    HlmPopoverImports,
    HlmIconImports,
    HlmInputGroupImports,
  ],
  providers: [
    O2_AUTOCOMPLETE_VALUE_ACCESSOR,
    provideIcons({ lucideSearch, lucideChevronDown, lucideCircleX }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let transformer = transformOptionToValue();
    <hlm-popover
      #popover
      align="start"
      autoFocus="first-heading"
      sideOffset="5"
      closeDelay="100"
      closeOnOutsidePointerEvents="true"
      (closed)="_closed()"
    >
      <div brnAutocomplete (selectionCleared)="_selectionCleared()">
        <div
          hlmInputGroup
          hlmAutocompleteTrigger
          [class]="_computedAutocompleteSearchClass()"
          [disabledTrigger]="_disabled()"
        >
          <input
            #input
            brnAutocompleteSearchInput
            hlmInputGroupInput
            type="text"
            autocomplete="off"
            [id]="inputId()"
            [class]="_computedAutocompleteInputClass()"
            [placeholder]="searchPlaceholderText()"
            [disabled]="_disabled()"
            [value]="search()"
            (input)="_searchChanged($event)"
            (mousedown)="_onMouseDown()"
            (focus)="_onFocus()"
          />
          <div hlmInputGroupAddon>
            <ng-icon name="lucideSearch" />
          </div>
          <div hlmInputGroupAddon align="inline-end">
            @if (showClearBtn() && _hasClearableContent()) {
              <button
                hlmInputGroupButton
                type="button"
                tabindex="-1"
                aria-label="clear"
                [disabled]="_disabled()"
                (click)="_clearAll()"
                size="icon-xs"
              >
                <ng-icon name="lucideCircleX" />
              </button>
            }

            @if (showChevron()) {
              <button
                hlmInputGroupButton
                type="button"
                tabindex="-1"
                [attr.aria-label]="ariaLabelToggleButton()"
                [disabled]="_disabled()"
                (click)="_toggleOptions()"
                size="icon-xs"
              >
                <ng-icon name="lucideChevronDown" />
              </button>
            }
          </div>
        </div>

        <div
          *brnPopoverContent="let ctx"
          hlmPopoverContent
          class="p-0"
          [style.width.px]="_elementRef.nativeElement.offsetWidth"
        >
          <hlm-autocomplete-list
            [class]="_computedAutocompleteListClass()"
            [class.hidden]="filteredOptions().length === 0"
          >
            <hlm-autocomplete-group>
              @for (option of filteredOptions(); track option) {
                <button
                  hlm-autocomplete-item
                  [class]="_computedAutocompleteItemClass()"
                  [value]="transformer ? transformer(option) : option"
                  (selected)="_optionSelected(option)"
                >
                  @if (optionTemplate(); as optionTemplate) {
                    <ng-container
                      *ngTemplateOutlet="optionTemplate; context: { $implicit: option }"
                    />
                  } @else {
                    {{ transformOptionToString()(option) }}
                  }
                </button>
              }
            </hlm-autocomplete-group>
          </hlm-autocomplete-list>

          <div
            *brnAutocompleteEmpty
            hlmAutocompleteEmpty
            [class]="_computedAutocompleteEmptyClass()"
          >
            @if (loading()) {
              <ng-content select="[loading]">{{ loadingText() }}</ng-content>
            } @else {
              <ng-content select="[empty]">{{ emptyText() }}</ng-content>
            }
          </div>
        </div>
      </div>
    </hlm-popover>
  `,
})
export class O2AutocompleteComponent<
  T extends O2AutocompleteOptionBase = O2AutocompleteOptionBase,
  V = T
> implements ControlValueAccessor, OnDestroy {
  private static _id = 0;

  private readonly _brnAutocomplete = viewChild.required(BrnAutocomplete);

  private readonly _inputRef = viewChild.required("input", {
    read: ElementRef,
  });

  protected readonly _elementRef = inject(ElementRef<HTMLElement>);

  /** Custom class for the autocomplete search container. */
  public readonly autocompleteSearchClass = input<ClassValue>("");
  protected readonly _computedAutocompleteSearchClass = computed(() =>
    hlm(this.autocompleteSearchClass())
  );

  /** Custom class for the autocomplete input. */
  public readonly autocompleteInputClass = input<ClassValue>("");
  protected readonly _computedAutocompleteInputClass = computed(() =>
    hlm(this.autocompleteInputClass())
  );

  /** Custom class for the autocomplete list. */
  public readonly autocompleteListClass = input<ClassValue>("");
  protected readonly _computedAutocompleteListClass = computed(() =>
    hlm(this.autocompleteListClass())
  );

  /** Custom class for each autocomplete item. */
  public readonly autocompleteItemClass = input<ClassValue>("");
  protected readonly _computedAutocompleteItemClass = computed(() =>
    hlm(this.autocompleteItemClass())
  );

  /** Custom class for the empty and loading state container. */
  public readonly autocompleteEmptyClass = input<ClassValue>("");
  protected readonly _computedAutocompleteEmptyClass = computed(() =>
    hlm(this.autocompleteEmptyClass())
  );

  /** The list of filtered options to display in the autocomplete. */
  public readonly filteredOptions = input<T[]>([]);

  /** The selected value. */
  public readonly value = model<T | V>();

  /** Debounce time in milliseconds for the search input. Default: 150ms */
  public readonly debounceTime = input<number>(150);

  /** The search query. */
  public readonly search = model<string>("");

  /** Debounced search query. */
  protected readonly _search = debouncedSignal(this.search, this.debounceTime());

  /**
   * Function to transform an option value to a search string.
   * O2 CUSTOM: Defaults to using displayName property.
   */
  public readonly transformValueToSearch = input<(option: T) => string>(
    defaultTransformToString
  );

  /** Whether selection of an option is required. Default: false */
  public readonly requireSelection = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /**
   * Function to transform an option to a display string.
   * O2 CUSTOM: Defaults to using displayName property.
   */
  public readonly transformOptionToString = input<(option: T) => string>(
    defaultTransformToString
  );

  /** Function to transform the object to the value. Default: undefined (uses option as-is) */
  public readonly transformOptionToValue = input<
    ((option: T) => V) | undefined
  >(undefined);

  /** Function to display the selected value as a string. */
  public readonly displayWith = input<((value: V) => string) | undefined>(
    undefined
  );

  /** Computed function to get the display value for the selected option. */
  protected readonly _displaySearchValue = computed(() => {
    const displayWith = this.displayWith();
    if (displayWith) {
      return displayWith;
    } else {
      return this.transformValueToSearch();
    }
  });

  /** Optional template for rendering each option. */
  public readonly optionTemplate =
    input<TemplateRef<O2AutocompleteOption<T>>>();

  /** Whether the autocomplete is in a loading state. */
  public readonly loading = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /**
   * Whether to show the clear button.
   * O2 CUSTOM: Shows when there's text in search OR a selected value (not just value like original).
   * Default: true (changed from false in original)
   */
  public readonly showClearBtn = input<boolean, BooleanInput>(true, {
    transform: booleanAttribute,
  });

  /**
   * Whether to show the chevron (dropdown toggle) button.
   * O2 CUSTOM: Default false since dropdown opens on focus/click anyway.
   */
  public readonly showChevron = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /** Placeholder text for the input field. */
  public readonly searchPlaceholderText = input<string>("Select an option");

  /** Text to display when loading options. */
  public readonly loadingText = input<string>("Loading options...");

  /** Text to display when no options are found. */
  public readonly emptyText = input<string>("No options found");

  /** Aria label for the toggle button. */
  public readonly ariaLabelToggleButton = input<string>("Toggle options");

  /** The id of the input field. */
  public readonly inputId = input<string>(
    `o2-autocomplete-input-${++O2AutocompleteComponent._id}`
  );

  /** Whether the autocomplete is disabled. */
  public readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  protected readonly _disabled = linkedSignal(() => this.disabled());

  /** Emitted when the selected value changes. */
  public readonly valueChange = output<T | V | null>();

  /** Emitted when the search query changes. */
  public readonly searchChange = output<string>();

  protected _onChange?: ChangeFn<T | V | null>;
  protected _onTouched?: TouchFn;

  /**
   * O2 CUSTOM: Computed signal to check if there's content to clear.
   * Returns true if there's text in search OR a selected value.
   */
  protected readonly _hasClearableContent = computed(() => {
    return this.search().length > 0 || this.value() !== undefined;
  });

  /** Flag to track if a recent click occurred to avoid double-triggering with focus */
  private _recentClick = false;

  /** Flag to track if Tab key was recently pressed (for distinguishing user Tab navigation from autoFocus) */
  private _recentTabPress = false;

  constructor() {
    classes(() => "block w-full");
    effect(() => {
      const search = this._search();
      this.searchChange.emit(search);
    });

    // O2 CUSTOM: Track Tab key presses to distinguish user Tab navigation from dialog autoFocus
    document.addEventListener("keydown", this._onKeyDown);
  }

  /**
   * O2 CUSTOM: Track Tab key press to enable focus-to-open only for user Tab navigation.
   * This prevents dropdown from opening when dialog/sheet autoFocus focuses the input.
   */
  private _onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Tab") {
      this._recentTabPress = true;
      setTimeout(() => {
        this._recentTabPress = false;
      }, 100);
    }
  };

  ngOnDestroy(): void {
    document.removeEventListener("keydown", this._onKeyDown);
  }

  protected _searchChanged(event: Event) {
    const inputValue = (event.target as HTMLInputElement).value;
    this.search.set(inputValue ?? "");

    // O2 CUSTOM: Clear selection if search text no longer matches selected value
    const currentValue = this.value();
    if (currentValue) {
      const displayWith = this.displayWith();
      const selectedDisplayName = displayWith
        ? displayWith(currentValue as V)
        : this.transformValueToSearch()(currentValue as T);
      if (inputValue !== selectedDisplayName) {
        // Search text doesn't match selected value - clear selection
        this.value.set(undefined);
        this._onChange?.(null);
        this.valueChange.emit(null);
      }
    }

    if (!this._brnAutocomplete().isExpanded() && inputValue.length > 0) {
      this._brnAutocomplete().open();
    }
  }

  /**
   * O2 CUSTOM: Track mousedown to distinguish click from Tab focus.
   */
  protected _onMouseDown(): void {
    this._recentClick = true;
    // Reset after a short delay
    setTimeout(() => {
      this._recentClick = false;
    }, 100);
  }

  /**
   * O2 CUSTOM: Handle input focus - open dropdown only on user Tab navigation.
   * Ignores:
   * - Focus triggered by click (popover trigger handles that)
   * - Focus triggered by dialog/sheet autoFocus (no recent Tab press)
   */
  protected _onFocus(): void {
    // Skip if focus was triggered by a click (popover trigger handles it)
    if (this._recentClick) {
      return;
    }
    // Only open on Tab navigation (not on dialog autoFocus)
    if (!this._recentTabPress) {
      return;
    }
    // Open dropdown on Tab navigation
    if (!this._disabled() && !this._brnAutocomplete().isExpanded()) {
      if (this.filteredOptions().length > 0) {
        this._brnAutocomplete().open();
      }
    }
  }

  /** Toggle the options panel */
  protected _toggleOptions() {
    if (this.filteredOptions().length > 0) {
      this._brnAutocomplete().toggle();
    }

    this._inputRef().nativeElement.focus();
  }

  /** Clear the current selection and search input (original behavior) */
  protected _selectionCleared() {
    this.value.set(undefined);
    this._onChange?.(null);
    this.valueChange.emit(null);
    this.search.set("");
  }

  /**
   * O2 CUSTOM: Clear everything - both search text and selected value.
   * This is triggered by the X button.
   */
  protected _clearAll() {
    this.value.set(undefined);
    this._onChange?.(null);
    this.valueChange.emit(null);
    this.search.set("");
    this._inputRef().nativeElement.focus();
  }

  protected _optionSelected(option: T) {
    const transformer = this.transformOptionToValue();

    const value = transformer ? transformer(option) : option;

    this.value.set(value);
    this._onChange?.(value);
    this.valueChange.emit(value);

    const searchValue = this._displaySearchValue()(value as any);
    this.search.set(searchValue ?? "");
    this._brnAutocomplete().close();
  }

  /** CONTROL VALUE ACCESSOR */
  public writeValue(value: T | V | null): void {
    this.value.set(value ? value : undefined);

    const searchValue = value ? this._displaySearchValue()(value as any) : "";
    this.search.set(searchValue);
  }

  public registerOnChange(fn: ChangeFn<T | V | null>): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: TouchFn): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
  }

  protected _closed() {
    if (this.requireSelection()) {
      const value = this.value();
      const searchValue = value
        ? this._displaySearchValue()(value as any)
        : "";
      this.search.set(searchValue ?? "");
    }
  }
}

export interface O2AutocompleteOption<T> {
  $implicit: T;
}
